import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth.jsx';
import { AppShell } from './components/layout/AppShell.jsx';
import { LoginPage } from './views/LoginPage.jsx';
import { DashboardPage } from './views/DashboardPage.jsx';
import { SpeakerListPage } from './views/speakers/SpeakerListPage.jsx';
import { SpeakerDetailPage } from './views/speakers/SpeakerDetailPage.jsx';
import { SpeakerFormPage } from './views/speakers/SpeakerFormPage.jsx';
import { EventListPage } from './views/events/EventListPage.jsx';
import { EventFormPage } from './views/events/EventFormPage.jsx';
import { EngagementListPage } from './views/engagements/EngagementListPage.jsx';
import { EngagementDetailPage } from './views/engagements/EngagementDetailPage.jsx';
import { EngagementFormPage } from './views/engagements/EngagementFormPage.jsx';
import { SpeakerPortalPage } from './views/portal/SpeakerPortalPage.jsx';
import { ExportPage } from './views/export/ExportPage.jsx';
import { UserAdminPage } from './views/admin/UserAdminPage.jsx';
import { OrgAdminPage } from './views/admin/OrgAdminPage.jsx';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) {
    // Preserve the URL hash (e.g. #invite_token=…, #recovery_token=…) — the
    // Identity widget reads window.location.hash live when ni.init() runs,
    // not a value captured when its script first loaded. Stripping the hash
    // here (a plain "/login" target) makes the widget find no token and
    // silently skip its auto-open behavior for invite/reset links.
    return <Navigate to={`/login${window.location.hash}`} replace />;
  }
  return children;
}

function RequireAdmin({ children }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to={`/login${window.location.hash}`} replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
    <BrowserRouter>
      <Routes>
        {/* Public: speaker portal (standalone — no AppShell) */}
        <Route path="/portal/:token" element={<SpeakerPortalPage />} />

        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected: all chapter-user routes inside AppShell */}
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          <Route path="/speakers" element={<SpeakerListPage />} />
          <Route path="/speakers/new" element={<SpeakerFormPage />} />
          <Route path="/speakers/:id" element={<SpeakerDetailPage />} />
          <Route path="/speakers/:id/edit" element={<SpeakerFormPage />} />

          <Route path="/events" element={<EventListPage />} />
          <Route path="/events/new" element={<EventFormPage />} />
          <Route path="/events/:id/edit" element={<EventFormPage />} />

          <Route path="/engagements" element={<EngagementListPage />} />
          <Route path="/engagements/new" element={<EngagementFormPage />} />
          <Route path="/engagements/:id" element={<EngagementDetailPage />} />
          <Route path="/engagements/:id/edit" element={<EngagementFormPage />} />

          <Route path="/export" element={<ExportPage />} />

          {/* Admin-only */}
          <Route
            path="/admin/users"
            element={
              <RequireAdmin>
                <UserAdminPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/orgs"
            element={
              <RequireAdmin>
                <OrgAdminPage />
              </RequireAdmin>
            }
          />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
    </AuthProvider>
  );
}
